import SeoHead from '@atoms/SeoHead';
import AboutMeHeader from '@molecules/AboutMeHeader';
import ColumnLayout from '@molecules/ColumnLayout';
import type { ColumnLayoutType } from '@molecules/ColumnLayout/ColumnLayout.types';
import Layout from '@molecules/Layout';
import type { LayoutProps } from '@molecules/Layout/Layout.types';
import { getClient } from '@sanity/lib/getClient';
import { ContentWrapper } from '@views/ArticlePageView/ArticlePageView.styled';
import type { GetStaticProps } from 'next';
import type { ReactElement } from 'react';

interface AboutMeProps {
  columnLayout: ColumnLayoutType;
  layoutData: Omit<LayoutProps, 'children'>;
}

const AboutMe = ({ layoutData, columnLayout }: AboutMeProps): ReactElement => (
  <>
    <SeoHead
      metaDescription="Witaj w moim małym miejscu pośród internetowej przestrzeni. Znajdziesz tu różne treści o tematyce podróżniczej."
      title="👩‍🦰 O mnie i o blogu"
    />
    <Layout {...layoutData}>
      <ColumnLayout
        {...columnLayout}
        breadcrumbs={{
          breadcrumbs: [
            {
              title: '🏠 Strona główna',
              link: '/',
            },
            {
              title: 'O mnie i o blogu',
              link: '/o-mnie/',
            },
          ],
        }}
      >
        <ContentWrapper>
          <AboutMeHeader
            description="Witaj w moim małym miejscu pośród internetowej przestrzeni. Znajdziesz tu różne treści o tematyce podróżniczej."
            image={{
              alt: 'Zdjęcie autorki bloga',
              src: '/static/images/about-me-page.webp',
            }}
          />
          <p>
            Na co dzień spełniam się na sali szkoleniowej. Podróże to moje
            hobby, a tutaj dzielę się wszystkim tym, co uważam za istotne,
            potrzebne i ciekawe, gdy chcesz obrać dany kierunek na urlop.
          </p>
          <p>W podróżach najbardziej kocham:</p>
          <h2>Odkrywać</h2>
          <p>
            Nowe miasta, plaże i zarośla. Wspinać się tam, skąd panorama zapiera
            dech. Włazić tam, gdzie czeka dobra sztuka, pyszne jedzenie i szybki
            roller coaster. Każdą podróż znajdziesz w zakładce &quot;Kierunki
            podróży&quot; na górze strony.
          </p>
          <h2>Organizować</h2>
          <p>
            Podobno każda wyprawa zaczyna się gdy o niej pomyślisz, trwa, kiedy
            się do niej przygotowujesz i wreszcie przesiąkasz nią, gdy
            przeżywasz ją na miejscu. Nie kończy się nigdy, bo pocztówki w
            Twojej głowie zostaną już na zawsze.
          </p>
          <p>
            Chętnie planuję, szukam, sprawdzam na własnej skórze. To co istotne
            opisuję tutaj. Od kupna tanich lotów, po tipy do wykorzystania na
            miejscu. Bo po co popełniać czyjeś błędy, skoro można taniej,
            lepiej, bezpieczniej, prawda? Wszystko w zakładce
            &quot;Poradnik&quot;.
          </p>
          <h2>Przygodować</h2>
          <p>
            “Nie wraca się tą samą drogą” powiedziałam i dzień później byłam na
            Olimpie, o którym wcześniej pomyślałam, że chciałabym tam być. Po
            prosecco powstają najlepsze historie, a życie daje nam zawsze różne
            scenariusze do wyboru. I o tym życiu znajdziesz trochę w zakładce
            &quot;Life&quot;.
          </p>
          <p>Wszystko tu jest moje, powstaje bez spiny, na luzie.</p>
          <p>Zapraszam!</p>
          <p>
            <strong>Magda</strong>
          </p>
        </ContentWrapper>
      </ColumnLayout>
    </Layout>
  </>
);

export const getStaticProps: GetStaticProps<AboutMeProps> = async () => {
  const client = getClient();
  const { getBasicData, getAllCategories } = await import('@lib/sanity');
  const { mapLayout } = await import('@mappers/layout');
  const { mapColumnLayout } = await import('@mappers/columnLayout');

  const tags = await getAllCategories(client);
  const basicData = await getBasicData(client);

  return {
    props: {
      layoutData: mapLayout(tags),
      columnLayout: mapColumnLayout(basicData),
    },
  };
};

export default AboutMe;
